// Common
import { Directive, ViewContainerRef, OnInit, OnDestroy, ComponentRef, inject, type Type } from '@angular/core';

// RX
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

// Services
import { ModalService } from '../services/modal.service';
import { ModalOpenRegistry } from '../services/modal-open-registry.service';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';

// Components
import { ModalWrapperComponent } from '../components/modal-wrapper/modal-wrapper.component';

// Types
import { ModalConfig } from '../types/modal-config';
import { Constants } from '@frontendlib/common/types/Constants';

@Directive({
  selector: '[appModalHost]',
  standalone: false,
})
export class ModalHostDirective implements OnInit, OnDestroy {
  // Private
  private alive = new Subject<void>();
  private minimizedModals: ComponentRef<ModalWrapperComponent>[] = [];
  private openModals: ComponentRef<ModalWrapperComponent>[] = [];
  private openItemsByKey = new Map<string, ComponentRef<ModalWrapperComponent>>();
  // Tracks all item keys associated with each modal for proper cleanup on close.
  // A modal may acquire additional keys after creation (e.g., when a new item is saved and gets an ID).
  private modalToItemKeys = new Map<ComponentRef<ModalWrapperComponent>, Set<string>>();
  // Draft keys a hidden modal gave up, so showing it again can reclaim those no other modal has taken.
  // Real item keys stay bound while hidden, so the same item cannot be opened twice.
  private hiddenDraftKeys = new Map<ComponentRef<ModalWrapperComponent>, Set<string>>();
  private readonly toasterService = inject(ToasterService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly modalService = inject(ModalService);
  private readonly modalRegistry = inject(ModalOpenRegistry);
  private readonly unregisterModals = new Set<() => void>();

  /**
   * Directive lifecycle
   */

  ngOnInit(): void {
    this.modalService
      .getDisplay()
      .pipe(takeUntil(this.alive))
      .subscribe((data: ModalConfig) => {
        const itemKey = this.getItemKey(data);

        // Check by item key (for items with id)
        if (itemKey) {
          const existingModal = this.openItemsByKey.get(itemKey);
          if (existingModal) {
            if (this.isHidden(existingModal)) {
              return;
            }
            if (existingModal.instance.minimized) {
              existingModal.instance.toggleCollapse();
            }
            this.bringModalToFront(existingModal);
            return;
          }
        }

        if (!this.isAvailableForCreatingMore(data)) {
          const max = data.maxModals || Constants.MODAL_MAX_OPEN;
          this.toasterService.show({
            text: `Maximum of ${max} dialogs can be open at the same time.`,
          });
          return;
        }

        const wrapperComponentRef = this.viewContainerRef.createComponent(ModalWrapperComponent, {
          injector: data.injector,
        });
        const unregisterModal = this.modalRegistry.register();
        this.unregisterModals.add(unregisterModal);

        wrapperComponentRef.instance.overlaps = this.isModalExists(data.component);
        wrapperComponentRef.instance.childComponent = data.component;
        wrapperComponentRef.instance.childComponentData = data.content;
        wrapperComponentRef.instance.resize = data.resize ?? true;
        wrapperComponentRef.instance.title = data.title;
        wrapperComponentRef.instance.appearance = data.appearance || 'amethyst';
        wrapperComponentRef.instance.frame = data.frame || {
          x: 'calc(50% - 400px)',
          y: 'calc(50% - 250px)',
          width: '800px',
          height: '500px',
        };
        wrapperComponentRef.instance.collapsible = data.collapsible;
        wrapperComponentRef.instance.expandable = data.expandable;
        wrapperComponentRef.instance.icon = data.icon;
        wrapperComponentRef.instance.closeTestId = data.closeTestId;

        // Track by item key if available (for items with id)
        if (itemKey) {
          this.openItemsByKey.set(itemKey, wrapperComponentRef);
          this.trackKeyForModal(wrapperComponentRef, itemKey);
        }

        this.openModals.push(wrapperComponentRef);
        this.updateZIndexes();

        // Safety net: guarantee cleanup runs regardless of how the component is destroyed.
        // Previously, cleanup only happened via the onClose subscription, which could be skipped
        // if the component was destroyed by Angular lifecycle without emitting onClose (e.g., route change).
        // This caused the modal count to leak, eventually blocking new modals with the "Maximum of N" toast.
        wrapperComponentRef.onDestroy(() => {
          unregisterModal();
          this.unregisterModals.delete(unregisterModal);
          this.removeFromOpenModals(wrapperComponentRef);
          this.removeFromMinimized(wrapperComponentRef);
          this.cleanupModalKeys(wrapperComponentRef);
          this.hiddenDraftKeys.delete(wrapperComponentRef);
        });

        wrapperComponentRef.instance.onClose.pipe(take(1), takeUntil(this.alive)).subscribe(() => {
          wrapperComponentRef.destroy();
        });

        wrapperComponentRef.instance.collapsed.pipe(takeUntil(this.alive)).subscribe((minimized) => {
          this.collapseModal(wrapperComponentRef, minimized);
        });

        wrapperComponentRef.instance.hiddenChange.pipe(takeUntil(this.alive)).subscribe((hidden) => {
          if (hidden) {
            this.hideModal(wrapperComponentRef);
          } else {
            this.showModal(wrapperComponentRef);
          }
        });

        wrapperComponentRef.instance.focused.pipe(takeUntil(this.alive)).subscribe(() => {
          this.bringModalToFront(wrapperComponentRef);
        });

        // When an item is saved, track the key so duplicate prevention works
        wrapperComponentRef.instance.titleUpdated.pipe(takeUntil(this.alive)).subscribe((event) => {
          this.hiddenDraftKeys.get(wrapperComponentRef)?.clear();

          // A create's real key (`${itemType}-${id}`) supersedes this wrapper's draft key
          // (#1307 round 2): once the item has an id, the draft key must stop pointing here,
          // or the next "New …" click would raise this now-saved form instead of opening a
          // fresh one.
          this.purgeDraftKeys(wrapperComponentRef);

          if (event.key && !this.openItemsByKey.has(event.key)) {
            this.openItemsByKey.set(event.key, wrapperComponentRef);
            this.trackKeyForModal(wrapperComponentRef, event.key);
          }
        });
      });
  }

  ngOnDestroy() {
    for (const unregister of this.unregisterModals) unregister();
    this.unregisterModals.clear();
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Methods
   */

  // Derives modal counts from the openModals array using class reference identity (===).
  // Previously used a separate Record<string, number> counter keyed by component.name,
  // which was unreliable in production builds where class names are mangled by terser/esbuild,
  // causing unrelated component types to share the same key and interfere with each other's limits.

  private isModalExists(component: Type<unknown>): boolean {
    return this.openModals.some((m) => m.instance.childComponent === component);
  }

  private isAvailableForCreatingMore(data: ModalConfig): boolean {
    // Global limit: no more than MODAL_MAX_OPEN total dialogs of any type
    if (this.openModals.length >= Constants.MODAL_MAX_OPEN) {
      return false;
    }
    // Per-type limit: respect maxModals if explicitly set
    const maxSameModal = data.maxModals || Constants.MODAL_MAX_OPEN;
    const currentCount = this.openModals.filter((m) => m.instance.childComponent === data.component).length;
    return currentCount < maxSameModal;
  }

  private getItemKey(data: ModalConfig): string | null {
    const stitchItem = data.content?.stitchItem;
    if (stitchItem?.itemType && stitchItem?.id) {
      return `${stitchItem.itemType}-${stitchItem.id}`;
    }

    // A fresh item has no id yet — e.g. the rail's "New note"/"New task" buttons build one
    // in memory (`getStitchInstanceByStitchType`) purely to open the create form, never
    // persisting it until Save (#1307 round 2). Without a key of its own, every click created
    // a brand new `FullFormModalComponent` instead of raising the one already open: two clicks
    // on the rail button opened two note windows. The `draft:` prefix keeps this key's shape
    // apart from a real `${itemType}-${id}` key (which never contains a colon), so a draft can
    // never collide with, or be confused for, a saved item.
    // A form opened for a picked file carries that file in its content, so it never shares a
    // draft key: raising an open draft would silently drop the file.
    if (stitchItem?.itemType && !data.content?.pickedFile) {
      return `draft:${stitchItem.itemType}`;
    }
    return null;
  }

  /** Removes every `draft:` key tracked for `component` — see the `titleUpdated` subscription. */
  private purgeDraftKeys(component: ComponentRef<ModalWrapperComponent>): void {
    const trackedKeys = this.modalToItemKeys.get(component);
    if (!trackedKeys) {
      return;
    }
    for (const key of trackedKeys) {
      if (key.startsWith('draft:')) {
        this.openItemsByKey.delete(key);
        trackedKeys.delete(key);
      }
    }
  }

  /** Releases a hidden modal's place in the ordering, the minimized row, the limits and its draft keys. */
  private hideModal(component: ComponentRef<ModalWrapperComponent>): void {
    if (this.isHidden(component)) {
      return;
    }
    const draftKeys = new Set<string>();
    for (const key of this.modalToItemKeys.get(component) ?? []) {
      if (key.startsWith('draft:')) {
        draftKeys.add(key);
      }
    }
    this.purgeDraftKeys(component);
    this.hiddenDraftKeys.set(component, draftKeys);
    this.removeFromOpenModals(component);
    this.removeFromMinimized(component);
    this.updateZIndexes();
  }

  private isHidden(component: ComponentRef<ModalWrapperComponent>): boolean {
    return this.hiddenDraftKeys.has(component);
  }

  /** Brings a hidden modal back expanded and in front, reclaiming only the draft keys still free. */
  private showModal(component: ComponentRef<ModalWrapperComponent>): void {
    const draftKeys = this.hiddenDraftKeys.get(component);
    if (!draftKeys) {
      return;
    }
    this.hiddenDraftKeys.delete(component);
    for (const key of draftKeys) {
      if (!this.openItemsByKey.has(key)) {
        this.openItemsByKey.set(key, component);
        this.trackKeyForModal(component, key);
      }
    }
    this.openModals.push(component);
    if (component.instance.minimized) {
      component.instance.toggleCollapse();
    }
    this.bringModalToFront(component);
  }

  private removeFromMinimized(component: ComponentRef<ModalWrapperComponent>): void {
    const removeIndex = this.minimizedModals.indexOf(component);
    if (removeIndex !== -1) {
      this.minimizedModals.splice(removeIndex, 1);
    }
    this.minimizedModals.forEach((value, index) => {
      value.instance.collapse(true, index);
    });
  }

  private collapseModal(component: ComponentRef<ModalWrapperComponent>, minimized: boolean): void {
    if (minimized) {
      const index = this.minimizedModals.push(component) - 1;
      component.instance.collapse(minimized, index);
      this.updateZIndexes(); // Update z-index so minimized modal goes to higher tier
    } else {
      this.removeFromMinimized(component);
      component.instance.collapse(minimized);
      this.bringModalToFront(component);
    }
  }

  private removeFromOpenModals(component: ComponentRef<ModalWrapperComponent>): void {
    const index = this.openModals.indexOf(component);
    if (index !== -1) {
      this.openModals.splice(index, 1);
    }
  }

  private bringModalToFront(component: ComponentRef<ModalWrapperComponent>): void {
    const index = this.openModals.indexOf(component);
    if (index === -1) {
      return;
    }
    // Move modal to end of array if not already there
    if (index !== this.openModals.length - 1) {
      this.openModals.splice(index, 1);
      this.openModals.push(component);
    }
    // Always update z-indexes (minimized state may have changed even if position didn't)
    this.updateZIndexes();
  }

  private updateZIndexes(): void {
    // Dynamic calculation ensures minimized modals are always above all regular modals
    const minimizedBaseZIndex = Constants.MODAL_BASE_Z_INDEX + this.openModals.length + 10;
    this.openModals.forEach((modal, index) => {
      if (modal.instance.minimized) {
        // Minimized modals get a higher z-index tier so they're always clickable
        const minimizedIndex = this.minimizedModals.indexOf(modal);
        if (minimizedIndex !== -1) {
          modal.instance.setZIndex(minimizedBaseZIndex + minimizedIndex);
        }
      } else {
        modal.instance.setZIndex(Constants.MODAL_BASE_Z_INDEX + index);
      }
    });
  }

  private trackKeyForModal(modal: ComponentRef<ModalWrapperComponent>, key: string): void {
    if (!this.modalToItemKeys.has(modal)) {
      this.modalToItemKeys.set(modal, new Set());
    }
    this.modalToItemKeys.get(modal)!.add(key);
  }

  private cleanupModalKeys(modal: ComponentRef<ModalWrapperComponent>): void {
    const trackedKeys = this.modalToItemKeys.get(modal);
    if (trackedKeys) {
      trackedKeys.forEach((key) => this.openItemsByKey.delete(key));
      this.modalToItemKeys.delete(modal);
    }
  }
}
